import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { Field, ErrorText, Spinner, btnPrimary, btnSecondary, inputCls, STATUS, VISIT_STATUSES } from "@/components/portal/ui";

const EMPTY = {
  patient_name: "",
  patient_phone: "",
  patient_address: "",
  specialty_id: "",
  doctor_id: "",
  nurse_id: "",
  date: format(new Date(), "yyyy-MM-dd"),
  start_time: "",
  status: "confirmed",
  notes: "",
};

export default function VisitDialog({ open, onOpenChange, visit, specialties, doctors, nurses, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [manual, setManual] = useState(false);
  const [slots, setSlots] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    if (visit) {
      setForm({ ...EMPTY, ...visit, nurse_id: visit.nurse_id || "" });
      setManual(true);
    } else {
      setForm({ ...EMPTY });
      setManual(false);
    }
  }, [open, visit]);

  useEffect(() => {
    if (!open || visit) return;
    setForm((f) => (f.specialty_id ? f : { ...f, specialty_id: specialties.find((s) => s.active)?.id || "" }));
  }, [open, visit, specialties]);

  useEffect(() => {
    if (!open || !form.specialty_id || !form.date) return;
    setSlots(null);
    api
      .get("/availability/slots", { params: { specialty_id: form.specialty_id, from: form.date, days: 1 } })
      .then((r) => setSlots(r.data[0]?.slots || []))
      .catch(() => setSlots([]));
  }, [open, form.specialty_id, form.date]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const doctorsForSpecialty = useMemo(
    () => doctors.filter((d) => d.status === "approved" && (!form.specialty_id || d.specialty_ids?.includes(form.specialty_id))),
    [doctors, form.specialty_id]
  );
  const approvedNurses = nurses.filter((n) => n.status === "approved");
  const selectedSlotKey = `${form.doctor_id}|${form.start_time}`;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.doctor_id || !form.start_time) {
      setError("Seleziona un orario disponibile oppure inserisci medico e ora manualmente.");
      return;
    }
    setSaving(true);
    const payload = { ...form, nurse_id: form.nurse_id || null };
    ["id", "end_time", "doctor_name", "nurse_name", "specialty_name", "source", "created_by", "created_at", "updated_at", "duration_minutes"].forEach((k) => delete payload[k]);
    try {
      const res = visit ? await api.put(`/visits/${visit.id}`, payload) : await api.post("/visits", payload);
      toast.success(visit ? "Visita aggiornata" : "Visita creata");
      onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-2xl" data-testid="visit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{visit ? "Modifica visita" : "Nuova visita"}</DialogTitle>
          <DialogDescription>Scegli specialità e data: ti mostriamo gli orari liberi in base alle disponibilità dei medici.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Paziente" htmlFor="visit-patient" className="sm:col-span-2">
            <input id="visit-patient" required value={form.patient_name} onChange={set("patient_name")} className={inputCls} placeholder="Nome e cognome" data-testid="visit-patient-input" />
          </Field>
          <Field label="Telefono" htmlFor="visit-phone">
            <input id="visit-phone" value={form.patient_phone} onChange={set("patient_phone")} className={inputCls} placeholder="+39 …" data-testid="visit-phone-input" />
          </Field>
          <Field label="Indirizzo visita" htmlFor="visit-address">
            <input id="visit-address" value={form.patient_address} onChange={set("patient_address")} className={inputCls} placeholder="Via, città" data-testid="visit-address-input" />
          </Field>
          <Field label="Specialità" htmlFor="visit-specialty">
            <select id="visit-specialty" required value={form.specialty_id} onChange={(e) => setForm((f) => ({ ...f, specialty_id: e.target.value, doctor_id: "", start_time: "" }))} className={inputCls} data-testid="visit-specialty-select">
              <option value="" disabled>Seleziona…</option>
              {specialties.filter((s) => s.active).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Data" htmlFor="visit-date">
            <input id="visit-date" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value, start_time: manual ? f.start_time : "" }))} className={inputCls} data-testid="visit-date-input" />
          </Field>

          <div className="sm:col-span-2 rounded-2xl bg-[#F6F4FA] p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#6B6280]">Orario e medico</p>
              <label className="flex items-center gap-2 text-xs font-semibold text-[#4A4059]">
                <Switch checked={manual} onCheckedChange={setManual} className="data-[state=checked]:bg-[#5B2C91]" data-testid="visit-manual-switch" />
                Inserimento manuale
              </label>
            </div>

            {manual ? (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <select value={form.doctor_id} onChange={set("doctor_id")} className={inputCls} data-testid="visit-doctor-select">
                  <option value="" disabled>Medico…</option>
                  {doctorsForSpecialty.map((d) => (
                    <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                  ))}
                </select>
                <input type="time" step={300} value={form.start_time} onChange={set("start_time")} className={inputCls} data-testid="visit-time-input" />
              </div>
            ) : slots === null ? (
              <div className="flex justify-center py-4"><Spinner /></div>
            ) : slots.length === 0 ? (
              <p className="mt-3 text-sm text-[#6B6280]" data-testid="visit-no-slots">Nessun orario libero per questa specialità in questa data. Cambia data oppure usa l'inserimento manuale.</p>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="visit-slot-list">
                {slots.map((s) => {
                  const key = `${s.doctor_id}|${s.start}`;
                  const active = key === selectedSlotKey;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => setForm((f) => ({ ...f, doctor_id: s.doctor_id, start_time: s.start }))}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition-colors ${active ? "bg-[#5B2C91] text-white ring-[#5B2C91]" : "bg-white text-[#1F152E] ring-[#5B2C91]/15 hover:ring-[#5B2C91]/40"}`}
                      data-testid={`visit-slot-${s.start}-${s.doctor_id}`}
                    >
                      {s.start} · {s.doctor_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <Field label="Infermiere" htmlFor="visit-nurse">
            <select id="visit-nurse" value={form.nurse_id} onChange={set("nurse_id")} className={inputCls} data-testid="visit-nurse-select">
              <option value="">Da assegnare</option>
              {approvedNurses.map((n) => (
                <option key={n.id} value={n.id}>{n.first_name} {n.last_name}</option>
              ))}
            </select>
          </Field>
          <Field label="Stato" htmlFor="visit-status">
            <select id="visit-status" value={form.status} onChange={set("status")} className={inputCls} data-testid="visit-status-select">
              {VISIT_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS[s].label}</option>
              ))}
            </select>
          </Field>
          <Field label="Note" htmlFor="visit-notes" className="sm:col-span-2">
            <textarea id="visit-notes" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} placeholder="Motivo della visita, indicazioni per l'infermiere…" data-testid="visit-notes-input" />
          </Field>

          <div className="sm:col-span-2"><ErrorText testid="visit-form-error">{error}</ErrorText></div>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className={btnSecondary} data-testid="visit-cancel-btn">Annulla</button>
            <button type="submit" disabled={saving} className={btnPrimary} data-testid="visit-save-btn">{saving ? "Salvataggio…" : visit ? "Salva modifiche" : "Crea visita"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
